// ─────────────────────────────────────────────────────────────
// VideoModal — shared onboarding video player.
// Plays one of two onboarding videos, selected by the `which`
// discriminator prop:
//   • "welcome"   — the intro video (context / orientation).
//   • "quickstart" — the quickstart walkthrough video.
// Used by WelcomeTab (the in-hero "Watch the intro" + "Quickstart"
// buttons) and HelpTab (sidebar "Watch the Intro" + "Watch the
// Quickstart" buttons). Autoplay is OK because every caller opens
// the modal in response to a user gesture (button click).
// The <video> element is conditionally rendered on `open` so
// closing the modal fully tears it down — playback stops + the
// network connection is released without needing a ref.
// ─────────────────────────────────────────────────────────────

import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

// Video URLs are absolute (prefixed with API_BASE) so the <video> element
// hits the backend directly rather than the Vite proxy. Matches how other
// direct-stream endpoints in this app construct URLs.
export const WELCOME_VIDEO_URL = `${API_BASE}/api/videos/welcome`;
export const QUICKSTART_VIDEO_URL = `${API_BASE}/api/videos/quickstart`;

/** Which onboarding video the modal plays. */
export type OnboardingVideo = "welcome" | "quickstart";

// Per-video config — keeps the URL + the aria-label in one place so the
// two videos stay in lockstep and adding a third is a one-line change.
const VIDEO_CONFIG: Record<
  OnboardingVideo,
  { url: string; ariaLabel: string }
> = {
  welcome: { url: WELCOME_VIDEO_URL, ariaLabel: "Welcome intro video" },
  quickstart: { url: QUICKSTART_VIDEO_URL, ariaLabel: "Quickstart video" },
};

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Which onboarding video to play. Defaults to "welcome" so existing
   * callers that haven't been updated keep their original behavior.
   */
  which?: OnboardingVideo;
}

export function VideoModal({ open, onClose, which = "welcome" }: VideoModalProps) {
  const { url, ariaLabel } = VIDEO_CONFIG[which];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-label={ariaLabel}
    >
      <DialogContent sx={{ p: 0, position: "relative", bgcolor: "common.black" }}>
        <IconButton
          onClick={onClose}
          aria-label="Close video"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            color: "common.white",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Conditional render so the video element is fully torn down on close —
            stops playback + releases the network connection without us needing
            to manage a ref. Keying on `which` as well means switching videos
            while open also remounts the element cleanly. */}
        {open && (
          <Box
            key={which}
            component="video"
            src={url}
            controls
            autoPlay
            sx={{
              width: "100%",
              maxHeight: "80vh",
              display: "block",
              aspectRatio: "16 / 9",
              bgcolor: "common.black",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
